import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { 
  Globe, 
  Settings, 
  User, 
  Trophy, 
  Crown, 
  Menu,
  X,
  ChevronRight,
  LogOut,
  ShoppingBag,
  Newspaper,
  Sparkles,
  Palette,
  Gift,
  Target,
  Home,
  Scale,
  Shield,
  FileText,
  AlertTriangle,
  Users,
  Package,
  MessageCircle
} from "lucide-react";

// Grouped menu items for better organization
const MENU_SECTIONS = [
  {
    title: "Play",
    items: [
      { id: 'worlds', label: 'Worlds', icon: Globe, color: 'from-purple-500 to-pink-500', emoji: '🌍' },
      { id: 'challenges', label: 'Challenges', icon: Target, color: 'from-blue-500 to-cyan-500', emoji: '🎯' },
      { id: 'inventory', label: 'Inventory', icon: Package, color: 'from-indigo-500 to-blue-500', emoji: '📦' },
    ]
  },
  {
    title: "Rewards",
    items: [
      { id: 'battle-pass', label: 'Battle Pass', icon: Sparkles, color: 'from-indigo-500 to-purple-500', emoji: '🎫' },
      { id: 'daily-spin', label: 'Daily Spin', icon: Gift, color: 'from-pink-500 to-red-500', emoji: '🎰' },
    ]
  },
  {
    title: "Store",
    items: [
      { id: 'shop', label: 'Shop', icon: ShoppingBag, color: 'from-amber-500 to-yellow-500', emoji: '🛒' },
      { id: 'exchange', label: 'Grand Exchange', icon: Scale, color: 'from-purple-500 to-indigo-500', emoji: '🏛️' },
      { id: 'cosmetics', label: 'Cosmetics', icon: Palette, color: 'from-rose-500 to-pink-500', emoji: '✨' },
    ]
  },
  {
    title: "Social",
    items: [
      { id: 'friends', label: 'Friends', icon: Users, color: 'from-cyan-500 to-blue-500', emoji: '👥' },
      { id: 'chat', label: 'Chat', icon: MessageCircle, color: 'from-green-500 to-teal-500', emoji: '💬' },
      { id: 'leaderboard', label: 'Leaderboard', icon: Crown, color: 'from-yellow-500 to-orange-500', emoji: '👑' },
      { id: 'profile', label: 'My Profile', icon: User, color: 'from-green-500 to-emerald-500', emoji: '👤' },
    ]
  },
  {
    title: "More",
    items: [
      { id: 'news', label: 'News', icon: Newspaper, color: 'from-red-500 to-pink-500', emoji: '📰' },
      { id: 'legal', label: 'Legal', icon: Scale, color: 'from-slate-500 to-slate-600', emoji: '⚖️' },
      { id: 'settings', label: 'Settings', icon: Settings, color: 'from-gray-500 to-slate-600', emoji: '⚙️' },
    ]
  }
];

const NEWS_ITEMS = [
  { id: 1, date: '13/02/2026', text: '🏛️ NEW WORLD: Ancient Ruins (181-200) with Pharaoh Boss!', icon: '🏛️', isNew: true },
  { id: 2, date: '13/02/2026', text: '❄️ NEW WORLD: Frozen Peaks (161-180) with Frost Giant!', icon: '❄️', isNew: true },
  { id: 3, date: '13/02/2026', text: '🌊 NEW WORLD: Ocean Depths (141-160) with Kraken Boss!', icon: '🌊', isNew: true },
  { id: 4, date: '13/02/2026', text: '📊 XP ADJUSTED: New tiered system - harder to level up!', icon: '📊', isNew: true },
  { id: 5, date: '13/02/2026', text: '🎮 60 NEW LEVELS (141-200) with unique obstacles!', icon: '🎮', isNew: true },
  { id: 6, date: '13/02/2026', text: '⚔️ GOD SWORD! Legendary item - clears entire board!', icon: '⚔️', isNew: false },
  { id: 7, date: '13/02/2026', text: '🍎 Fruit Drops! 99% chance to get fruits from levels!', icon: '🍎', isNew: false },
  { id: 8, date: '13/02/2026', text: '🪙 Fruit Exchange! Trade fruits for coins in Shop!', icon: '🪙', isNew: false },
  { id: 9, date: '12/02/2026', text: 'Grand Exchange - Player marketplace for items!', icon: '🏛️', isNew: false },
  { id: 10, date: '12/02/2026', text: 'Dark Forest World (121-140) with Owl Boss!', icon: '🦉', isNew: false },
];

export function SidebarMenu({ 
  isOpen, 
  onClose, 
  onMenuSelect,
  challengeCount = 0,
  currentStars = 0,
  canSpin = false
}) {
  const navigate = useNavigate();
  const [showNews, setShowNews] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [legalTab, setLegalTab] = useState('privacy');
  
  const handleItemClick = (itemId) => {
    if (itemId === 'profile') {
      navigate('/profile');
      onClose();
    } else if (itemId === 'challenges') {
      navigate('/challenges');
      onClose();
    } else if (itemId === 'leaderboard') {
      navigate('/leaderboard');
      onClose();
    } else if (itemId === 'news') {
      setShowNews(true);
    } else if (itemId === 'legal') {
      setShowLegal(true);
      setLegalTab('privacy');
    } else if (itemId === 'daily-spin') {
      onMenuSelect?.('daily-spin');
      onClose();
    } else {
      onMenuSelect?.(itemId);
      onClose();
    }
  };
  
  const newNewsCount = NEWS_ITEMS.filter(n => n.isNew).length;

  // Helper to get badge for an item
  const getBadge = (itemId) => {
    if (itemId === 'challenges' && challengeCount > 0) {
      return <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{challengeCount}</span>;
    }
    if (itemId === 'news' && newNewsCount > 0) {
      return <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{newNewsCount}</span>;
    }
    if (itemId === 'daily-spin' && canSpin) {
      return <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">FREE</span>;
    }
    if (itemId === 'shop') {
      return <span className="text-amber-400 text-xs font-bold">⭐ {currentStars}</span>;
    }
    return null;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div 
        className={cn(
          "fixed left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 z-50 shadow-2xl flex flex-col",
          isOpen ? "animate-menu-slide-in" : "-translate-x-full transition-transform duration-200"
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-white/10 bg-gradient-to-r from-orange-500/20 to-yellow-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-yellow-500 flex items-center justify-center shadow-lg">
                <span className="text-xl">🍌</span>
              </div>
              <div>
                <span className="font-bold text-white text-lg block">Fruit Crush</span>
                <span className="text-white/50 text-xs">Menu</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Scrollable Menu Items */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          <div className="p-3 space-y-4">
            {MENU_SECTIONS.map((section, sectionIndex) => (
              <div key={section.title}>
                {/* Section Title */}
                <div className="px-2 mb-2">
                  <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                    {section.title}
                  </span>
                </div>
                
                {/* Section Items */}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const badge = getBadge(item.id);
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}
                        className={cn(
                          "w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200",
                          "bg-white/5 hover:bg-white/15 active:scale-[0.98]",
                          "border border-transparent hover:border-white/10",
                          "group"
                        )}
                      >
                        {/* Icon with gradient background */}
                        <div className={cn(
                          "w-9 h-9 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110",
                          `bg-gradient-to-br ${item.color}`
                        )}>
                          <span className="text-base">{item.emoji}</span>
                        </div>
                        
                        {/* Label */}
                        <span className="flex-1 text-left font-medium text-white/90 text-sm">
                          {item.label}
                        </span>
                        
                        {/* Badge */}
                        {badge}
                        
                        {/* Arrow */}
                        <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                      </button>
                    );
                  })}
                </div>
                
                {/* Divider between sections (except last) */}
                {sectionIndex < MENU_SECTIONS.length - 1 && (
                  <div className="mt-3 border-b border-white/5" />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-3 border-t border-white/10 bg-black/20">
          <div className="flex items-center justify-between text-xs text-white/40">
            <span>Fruit Crush v3.2.5</span>
            <span>Season 1</span>
          </div>
        </div>
      </div>
      
      {/* News Modal */}
      {showNews && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70"
            onClick={() => setShowNews(false)}
          />
          <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl w-full max-w-sm border-2 border-pink-500/30 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-pink-500 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📰</span>
                  <h2 className="text-xl font-bold text-white">News & Updates</h2>
                </div>
                <button
                  onClick={() => setShowNews(false)}
                  className="p-1 rounded-full bg-white/20 hover:bg-white/30 text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* News List */}
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {NEWS_ITEMS.map((news) => (
                <div 
                  key={news.id}
                  className={cn(
                    "p-3 rounded-xl border transition-colors",
                    news.isNew 
                      ? "bg-pink-500/10 border-pink-500/30" 
                      : "bg-white/5 border-white/10"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{news.icon}</span>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{news.text}</p>
                      <p className="text-white/40 text-xs mt-1">{news.date}</p>
                    </div>
                    {news.isNew && (
                      <span className="bg-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                        NEW
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Legal Modal */}
      {showLegal && (
        <LegalModal 
          isOpen={showLegal}
          onClose={() => setShowLegal(false)}
          activeTab={legalTab}
          onTabChange={setLegalTab}
        />
      )}
    </>
  );
}

// Legal Modal Component
function LegalModal({ isOpen, onClose, activeTab, onTabChange }) {
  if (!isOpen) return null;
  
  const tabs = [
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'terms', label: 'Terms of Service', icon: FileText },
    { id: 'rules', label: 'Game Rules', icon: AlertTriangle },
  ];
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/80"
        onClick={onClose}
      />
      <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl max-h-[85vh] border-2 border-slate-500/30 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-slate-600 to-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">Legal Information</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-full bg-white/20 hover:bg-white/30 text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-white/10 bg-black/20">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all",
                  activeTab === tab.id
                    ? "bg-slate-600/50 text-white border-b-2 border-orange-400"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {activeTab === 'privacy' && <PrivacyPolicyContent />}
          {activeTab === 'terms' && <TermsOfServiceContent />}
          {activeTab === 'rules' && <GameRulesContent />}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-white/10 bg-black/20">
          <p className="text-center text-white/40 text-xs">
            Last updated: February 12, 2026 • Fruit Crush by FXC Vantage
          </p>
        </div>
      </div>
    </div>
  );
}

// Privacy Policy Content
function PrivacyPolicyContent() {
  return (
    <div className="prose prose-invert prose-sm max-w-none">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-6 h-6 text-blue-400" />
        <h2 className="text-xl font-bold text-white m-0">Privacy Policy</h2>
      </div>
      
      <p className="text-white/70 text-sm mb-6">
        Effective Date: February 12, 2026
      </p>
      
      <div className="space-y-6 text-white/80">
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">1. Introduction</h3>
          <p className="text-sm leading-relaxed">
            Welcome to Fruit Crush ("we," "our," or "us"). We are committed to protecting your privacy 
            and ensuring the security of your personal information. This Privacy Policy explains how we 
            collect, use, disclose, and safeguard your information when you use our mobile game application 
            and related services.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">2. Information We Collect</h3>
          <p className="text-sm leading-relaxed mb-2">We collect the following types of information:</p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2">
            <li><strong>Account Information:</strong> Email address, username, and encrypted password when you create an account.</li>
            <li><strong>Profile Information:</strong> Player name you choose to display in the game.</li>
            <li><strong>Game Data:</strong> Your progress, scores, achievements, level completions, XP, and in-game purchases.</li>
            <li><strong>Payment Information:</strong> When you make purchases, payment processing is handled securely by Stripe. We do not store your full credit card details.</li>
            <li><strong>Device Information:</strong> Device type, operating system, and unique device identifiers for security and analytics purposes.</li>
            <li><strong>Usage Data:</strong> How you interact with the game, including features used and time spent playing.</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">3. How We Use Your Information</h3>
          <p className="text-sm leading-relaxed mb-2">We use your information to:</p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2">
            <li>Provide, maintain, and improve the game experience</li>
            <li>Process your transactions and send related information</li>
            <li>Save your game progress and synchronize across devices</li>
            <li>Display your name on leaderboards (if you choose)</li>
            <li>Send you game-related notifications and updates</li>
            <li>Detect, prevent, and address cheating, fraud, and technical issues</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">4. Data Sharing and Disclosure</h3>
          <p className="text-sm leading-relaxed mb-2">We may share your information with:</p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2">
            <li><strong>Service Providers:</strong> Third-party services that help us operate the game (e.g., Stripe for payments, cloud hosting providers).</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and the safety of users.</li>
            <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets.</li>
          </ul>
          <p className="text-sm leading-relaxed mt-2">
            We do <strong>not</strong> sell your personal information to third parties for advertising purposes.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">5. Data Security</h3>
          <p className="text-sm leading-relaxed">
            We implement industry-standard security measures to protect your data, including encryption 
            of sensitive information, secure server infrastructure, and regular security audits. However, 
            no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">6. Your Rights</h3>
          <p className="text-sm leading-relaxed mb-2">You have the right to:</p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and associated data</li>
            <li>Opt out of promotional communications</li>
            <li>Export your game data</li>
          </ul>
          <p className="text-sm leading-relaxed mt-2">
            To exercise these rights, please contact us at: <span className="text-orange-400">info@fxcvantage.com</span>
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">7. Children's Privacy</h3>
          <p className="text-sm leading-relaxed">
            Fruit Crush is intended for general audiences. We do not knowingly collect personal information 
            from children under 13. If you are a parent or guardian and believe your child has provided us 
            with personal information, please contact us immediately.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">8. Changes to This Policy</h3>
          <p className="text-sm leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new policy in the app and updating the "Effective Date" above. Your continued 
            use of the game after changes constitutes acceptance of the updated policy.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">9. Contact Us</h3>
          <p className="text-sm leading-relaxed">
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <div className="bg-white/5 rounded-lg p-3 mt-2">
            <p className="text-sm text-white/90">Email: info@fxcvantage.com</p>
            <p className="text-sm text-white/90">Company: FXC Vantage</p>
          </div>
        </section>
      </div>
    </div>
  );
}

// Terms of Service Content
function TermsOfServiceContent() {
  return (
    <div className="prose prose-invert prose-sm max-w-none">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-6 h-6 text-green-400" />
        <h2 className="text-xl font-bold text-white m-0">Terms of Service</h2>
      </div>
      
      <p className="text-white/70 text-sm mb-6">
        Effective Date: February 12, 2026
      </p>
      
      <div className="space-y-6 text-white/80">
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">1. Acceptance of Terms</h3>
          <p className="text-sm leading-relaxed">
            By accessing or using Fruit Crush ("the Game"), you agree to be bound by these Terms of Service 
            ("Terms"). If you do not agree to these Terms, do not use the Game. We reserve the right to 
            modify these Terms at any time, and your continued use constitutes acceptance of any changes.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">2. Eligibility</h3>
          <p className="text-sm leading-relaxed">
            You must be at least 13 years old to create an account and use the Game. 
          </p>
          <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 mt-3">
            <p className="text-sm text-red-300 font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Important Age Requirement for Purchases
            </p>
            <p className="text-sm text-white/90 mt-1">
              <strong>You must be at least 18 years old to make any in-app purchases.</strong> By making a 
              purchase, you confirm that you are 18 years of age or older and have the legal capacity to 
              enter into binding contracts. If you are under 18, you may not make purchases without the 
              consent and supervision of a parent or legal guardian who agrees to be bound by these Terms.
            </p>
          </div>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">3. Account Registration</h3>
          <p className="text-sm leading-relaxed">
            To access certain features, you must create an account. You are responsible for:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2 mt-2">
            <li>Providing accurate and complete information</li>
            <li>Maintaining the security of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">4. In-App Purchases & Payment Terms</h3>
          <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg p-4 mb-3">
            <p className="text-sm text-amber-300 font-bold mb-2">ALL PURCHASES ARE FINAL AND NON-REFUNDABLE</p>
            <p className="text-sm text-white/90">
              When you make a purchase in Fruit Crush (including but not limited to Stars, Battle Pass, 
              or any other virtual items), you acknowledge and agree that:
            </p>
          </div>
          <ul className="list-disc list-inside text-sm space-y-2 ml-2">
            <li>
              <strong>No Refunds:</strong> All purchases are final. We do not offer refunds, returns, or 
              exchanges for any virtual currency, items, or features purchased in the Game, regardless of 
              whether they have been used.
            </li>
            <li>
              <strong>Virtual Items:</strong> All purchased items (Stars, Battle Pass rewards, cosmetics, 
              power-ups) are virtual items with no real-world monetary value and cannot be exchanged, 
              transferred, or redeemed for cash.
            </li>
            <li>
              <strong>Battle Pass:</strong> The Battle Pass is a seasonal purchase. Unused tiers or rewards 
              at the end of a season will expire and are not refundable or transferable to the next season.
            </li>
            <li>
              <strong>Price Changes:</strong> We reserve the right to change prices for virtual items at 
              any time without prior notice. Price changes do not affect previously completed purchases.
            </li>
            <li>
              <strong>Payment Processing:</strong> All payments are processed securely through Stripe. By 
              making a purchase, you agree to Stripe's terms of service.
            </li>
            <li>
              <strong>Chargebacks:</strong> Initiating a chargeback or payment dispute without first 
              contacting us may result in immediate account suspension or termination.
            </li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">5. License Grant</h3>
          <p className="text-sm leading-relaxed">
            We grant you a limited, non-exclusive, non-transferable, revocable license to use the Game 
            for personal, non-commercial entertainment purposes. This license does not include the right to:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2 mt-2">
            <li>Modify, adapt, or create derivative works of the Game</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Game</li>
            <li>Remove or alter any proprietary notices or labels</li>
            <li>Use the Game for any commercial purpose</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">6. User Conduct</h3>
          <p className="text-sm leading-relaxed mb-2">You agree not to:</p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2">
            <li>Use cheats, exploits, automation software, bots, hacks, or unauthorized third-party software</li>
            <li>Exploit bugs or glitches for unfair advantage</li>
            <li>Harass, abuse, or harm other players</li>
            <li>Impersonate any person or entity</li>
            <li>Engage in any activity that interferes with or disrupts the Game</li>
            <li>Attempt to gain unauthorized access to any systems or accounts</li>
            <li>Buy, sell, or trade accounts or virtual items outside the Game</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">7. Termination</h3>
          <p className="text-sm leading-relaxed">
            We reserve the right to suspend or terminate your account at any time, with or without notice, 
            for any reason, including but not limited to violation of these Terms. Upon termination:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2 mt-2">
            <li>Your license to use the Game immediately terminates</li>
            <li>You lose access to all virtual items and progress</li>
            <li>No refunds will be provided for any purchases made</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">8. Disclaimer of Warranties</h3>
          <p className="text-sm leading-relaxed">
            THE GAME IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM 
            ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
            AND NON-INFRINGEMENT. We do not guarantee that the Game will be uninterrupted, secure, or error-free.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">9. Limitation of Liability</h3>
          <p className="text-sm leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF DATA, PROFITS, OR GOODWILL, 
            ARISING FROM YOUR USE OF THE GAME.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">10. Governing Law</h3>
          <p className="text-sm leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the Netherlands, 
            without regard to its conflict of law provisions. Any disputes shall be resolved in the courts 
            of the Netherlands.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">11. Contact Information</h3>
          <p className="text-sm leading-relaxed">
            For questions about these Terms, please contact us at:
          </p>
          <div className="bg-white/5 rounded-lg p-3 mt-2">
            <p className="text-sm text-white/90">Email: info@fxcvantage.com</p>
            <p className="text-sm text-white/90">Company: FXC Vantage</p>
          </div>
        </section>
      </div>
    </div>
  );
}

// Game Rules Content
function GameRulesContent() {
  return (
    <div className="prose prose-invert prose-sm max-w-none">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-6 h-6 text-yellow-400" />
        <h2 className="text-xl font-bold text-white m-0">Game Rules & Fair Play Policy</h2>
      </div>
      
      <p className="text-white/70 text-sm mb-6">
        Effective Date: February 12, 2026
      </p>
      
      <div className="space-y-6 text-white/80">
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">1. Fair Play Commitment</h3>
          <p className="text-sm leading-relaxed">
            Fruit Crush is committed to providing a fair and enjoyable gaming experience for all players. 
            We expect all players to engage in fair play and maintain the integrity of the game. These 
            rules apply to all players, regardless of spending or experience level.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">2. Prohibited Activities</h3>
          <p className="text-sm leading-relaxed mb-2">
            The following activities are strictly prohibited and will result in penalties:
          </p>
          
          <div className="space-y-3 mt-3">
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
              <h4 className="text-red-400 font-semibold text-sm mb-1">Cheating & Hacking</h4>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Using third-party software, bots, or automation tools</li>
                <li>Modifying game files, memory, or network traffic</li>
                <li>Using speed hacks, infinite lives/moves exploits</li>
                <li>Injecting unauthorized code into the game client</li>
                <li>Using emulators with cheat features enabled</li>
              </ul>
            </div>
            
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
              <h4 className="text-red-400 font-semibold text-sm mb-1">Exploiting Bugs</h4>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Intentionally abusing bugs or glitches for unfair advantage</li>
                <li>Duplicating items, currency, or resources through exploits</li>
                <li>Sharing exploit methods with other players</li>
                <li>Failing to report known bugs (report to info@fxcvantage.com)</li>
              </ul>
            </div>
            
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
              <h4 className="text-red-400 font-semibold text-sm mb-1">Account Violations</h4>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Creating multiple accounts to gain unfair advantages</li>
                <li>Sharing, selling, or trading accounts</li>
                <li>Using another player's account without permission</li>
                <li>Account boosting services or win-trading</li>
              </ul>
            </div>
            
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
              <h4 className="text-red-400 font-semibold text-sm mb-1">Fraudulent Activities</h4>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Initiating chargebacks or payment disputes fraudulently</li>
                <li>Using stolen payment methods</li>
                <li>Attempting to obtain free items through deception</li>
                <li>Impersonating staff members or other players</li>
              </ul>
            </div>
          </div>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">3. Penalties & Enforcement</h3>
          
          <div className="bg-slate-700/50 rounded-lg p-4 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-red-400 font-bold">CHEATING RESULTS IN PERMANENT BAN</span>
            </div>
            <p className="text-sm text-white/90">
              We operate a <strong>zero-tolerance policy</strong> for cheating. Any player found to be 
              cheating, hacking, or using unauthorized software will receive an <strong>immediate and 
              permanent ban</strong> with no warnings.
            </p>
          </div>
          
          <div className="space-y-2">
            <div className="bg-white/5 rounded-lg p-3 border-l-4 border-yellow-500">
              <p className="text-sm"><strong className="text-yellow-400">First Minor Offense:</strong> Warning and temporary suspension (24-72 hours)</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border-l-4 border-orange-500">
              <p className="text-sm"><strong className="text-orange-400">Second Minor Offense:</strong> Extended suspension (7-30 days)</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border-l-4 border-red-500">
              <p className="text-sm"><strong className="text-red-400">Cheating/Major Offense:</strong> Permanent ban - no exceptions</p>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed mt-3">
            <strong>Banned accounts:</strong>
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2">
            <li>Will lose access to all game progress, items, and purchases</li>
            <li>Will NOT receive any refunds for previous purchases</li>
            <li>Cannot create new accounts (device and IP tracking in place)</li>
            <li>Appeals may be submitted but are rarely granted for cheating bans</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">4. Leaderboard Integrity</h3>
          <p className="text-sm leading-relaxed">
            Leaderboards are monitored for suspicious activity. Players with abnormal scores or impossible 
            achievements will be investigated. Leaderboard manipulation will result in:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2 mt-2">
            <li>Removal of illegitimate scores</li>
            <li>Leaderboard ranking reset</li>
            <li>Potential account suspension or ban</li>
            <li>Disqualification from events and rewards</li>
          </ul>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">5. Reporting Violations</h3>
          <p className="text-sm leading-relaxed">
            If you suspect another player is cheating or violating these rules, please report them to us:
          </p>
          <div className="bg-blue-500/20 border border-blue-500/40 rounded-lg p-3 mt-2">
            <p className="text-sm text-white/90 mb-2"><strong>How to Report:</strong></p>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Email: <span className="text-blue-400">info@fxcvantage.com</span></li>
              <li>Include the suspected player's username</li>
              <li>Describe the violation and provide evidence if possible</li>
              <li>Include screenshots or video recordings if available</li>
            </ul>
          </div>
          <p className="text-sm leading-relaxed mt-2">
            All reports are reviewed and kept confidential. False reports made with malicious intent may 
            result in penalties for the reporter.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">6. Detection Systems</h3>
          <p className="text-sm leading-relaxed">
            Fruit Crush employs various systems to detect cheating, including but not limited to:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2 mt-2">
            <li>Server-side validation of all game actions</li>
            <li>Pattern analysis and anomaly detection</li>
            <li>Device fingerprinting</li>
            <li>Network traffic monitoring</li>
            <li>Behavioral analysis algorithms</li>
          </ul>
          <p className="text-sm leading-relaxed mt-2 text-white/60">
            We continuously update our detection methods. Evading detection does not mean cheating is 
            undetected—bans may be applied retroactively.
          </p>
        </section>
        
        <section>
          <h3 className="text-lg font-semibold text-orange-400 mb-2">7. Appeals Process</h3>
          <p className="text-sm leading-relaxed">
            If you believe your account was banned in error, you may submit an appeal:
          </p>
          <ul className="list-disc list-inside text-sm space-y-1 ml-2 mt-2">
            <li>Email: <span className="text-orange-400">info@fxcvantage.com</span></li>
            <li>Include your account email and username</li>
            <li>Provide a detailed explanation</li>
            <li>Appeals are reviewed within 7-14 business days</li>
            <li>Decisions on appeals are final</li>
          </ul>
          <p className="text-sm leading-relaxed mt-2 text-red-400">
            Note: Appeals for confirmed cheating violations are almost never successful. We recommend 
            playing fair from the start.
          </p>
        </section>
        
        <section className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-4 border border-green-500/30">
          <h3 className="text-lg font-semibold text-green-400 mb-2">Play Fair, Have Fun!</h3>
          <p className="text-sm leading-relaxed text-white/90">
            The best way to enjoy Fruit Crush is to play fairly. Compete with other players on a level 
            playing field, earn your achievements honestly, and be part of a positive gaming community. 
            Thank you for helping us keep Fruit Crush fun for everyone!
          </p>
        </section>
      </div>
    </div>
  );
}

// Export MenuButton component for opening the sidebar
export function MenuButton({ onClick, className, hasNotification }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      data-testid="menu-button"
      className={cn(
        "bg-black/30 hover:bg-black/50 text-white rounded-xl backdrop-blur-sm border border-white/10 relative",
        className
      )}
    >
      <Menu className="w-5 h-5" />
      {hasNotification && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      )}
    </Button>
  );
}
